import { MagistralDrug } from '@dreryk/drerykweb-medicine-api';

import { Choice } from '@dreryk/watermelon';

import {
   calculateNewIssuedToLocalDate,
   changeIssuedToDateFromPastToTodayOnly,
   getIssuedFromDefaultValue,
   getQuantityDefaultValue,
   isIssuedForMoreThanMonth,
   MagistralDrugPrescription,
   mapAdditionalEntitlementsToAdditionalEntitlementsTypes,
   mapReimbursementToPrescriptionReimbursementType,
   selectAdditionalRequirementsTypes,
} from '~libs.shared.prescription';

import { DraftPrescriptionDTO } from '../../prescriptions.types';
import { PatientId } from '../../../PrescriptionIssuingPanel.types';
import { mapAdditionalRequirementsToRequirementTypes } from '../../prescription/additionalRequirements/additionalRequirements.utils';

import {
   DraftMagistralDrugPrescription,
   DraftMagistralDrugPrescriptionFieldName,
} from './DraftMagistralDrugPrescriptionField.types';

export const createDraftMagistralDrugPrescriptionsFieldNameFactory = (index: number) => (
   key: keyof DraftMagistralDrugPrescription['data']
): DraftMagistralDrugPrescriptionFieldName => `draftPrescriptions[${index}].data.${key}`;

export const mapMagistralDragFormDataToDraftMagistralDrugPrescription = (
   patientId: PatientId,
   magistralDrug: MagistralDrug
): DraftMagistralDrugPrescription => {
   const issuedForYear = false;
   const issuedFrom = getIssuedFromDefaultValue();
   const issuedTo = calculateNewIssuedToLocalDate(issuedFrom, issuedForYear);

   return {
      type: 'magistralDrug',
      sourceType: 'addedByUser',
      queryData: {
         patientId: patientId,
         magistralDrugId: magistralDrug.id!,
      },
      selectedByUser: {
         magistralDrug: magistralDrug,
      },
      initialData: {
         medicinePrescription: undefined,
         additionalEntitlementTypes: undefined,
         additionalRequirementsTypes: undefined,
         reimbursementType: undefined,
         quantity: getQuantityDefaultValue(),
         ruleValidationResults: undefined,
      },
      data: {
         dosage: null,
         reimbursement: null,
         quantity: getQuantityDefaultValue(),
         issuedFrom: issuedFrom,
         issuedTo: issuedTo,
         additionalEntitlements: null,
         additionalRequirements: null,
         medicineName: magistralDrug.name,
      },
   };
};

export const mapDraftMagistralDrugPrescriptionToDraftPrescriptionDTO = (
   prescription: DraftMagistralDrugPrescription
): DraftPrescriptionDTO => {
   const additionalRequirementsTypes = mapAdditionalRequirementsToRequirementTypes(
      prescription.data.additionalRequirements
   );

   return {
      additionalEntitlementTypes: mapAdditionalEntitlementsToAdditionalEntitlementsTypes(
         prescription.data.additionalEntitlements
      ),
      dosage: Choice.withUseDefaultIfNull(prescription.data.dosage),
      issuedFrom: prescription.data.issuedFrom!,
      issuedTo: prescription.data.issuedTo!,
      magistralDrugId: prescription.queryData.magistralDrugId,
      quantity: prescription.data.quantity ?? 0,
      reimbursementType: Choice.withUseDefaultIfNull(
         mapReimbursementToPrescriptionReimbursementType(prescription.data.reimbursement)
      ),
      substituteForbidden: additionalRequirementsTypes.includes('SUBSTITUTE_FORBIDDEN'),
      cito: false, // TODO: DREGAB-3969 Waiting for implementation - cito flag,
      treatmentDays: Choice.withUseDefault(),
      preciseDosage: Choice.withUseDefault(),
   };
};

export const mapPrescriptionToDraftMagistralDrugPrescription = (
   prescription: MagistralDrugPrescription,
   patientId: PatientId
): DraftMagistralDrugPrescription => {
   const issuedForYear = isIssuedForMoreThanMonth(prescription.issuedFrom, prescription.issuedTo);
   const issuedFrom = changeIssuedToDateFromPastToTodayOnly(prescription.issuedFrom);
   const issuedTo = calculateNewIssuedToLocalDate(issuedFrom, issuedForYear);

   return {
      type: 'magistralDrug',
      sourceType: 'fromServer',
      queryData: {
         patientId: patientId,
         magistralDrugId: prescription.medicinePrescription.magistralDrug.id!,
      },
      selectedByUser: {
         magistralDrug: undefined,
      },
      initialData: {
         additionalEntitlementTypes: prescription.additionalEntitlementTypes,
         medicinePrescription: undefined, // TODO: DREGAB-3763 Return prescription.medicinePrescription after fix MedicinePrescriptionQuery
         reimbursementType: prescription.reimbursementType,
         quantity: prescription.quantity,
         additionalRequirementsTypes: selectAdditionalRequirementsTypes({
            cito: false, // TODO: DREGAB-3969 Waiting for implementation - cito flag
            issuedForYear: issuedForYear,
            substituteForbidden: prescription.substituteForbidden,
         }),
         ruleValidationResults: prescription.ruleValidationResults,
      },
      data: {
         dosage: prescription.dosage ?? null,
         reimbursement: null,
         quantity: prescription.quantity,
         issuedFrom: issuedFrom,
         issuedTo: issuedTo,
         additionalEntitlements: null,
         additionalRequirements: null,
         medicineName: prescription.medicinePrescription.magistralDrug.name,
      },
   };
};
